import * as ES from './ecmascript';
import { MakeIntrinsicClass } from './intrinsicclass';
import { ISO_YEAR, ISO_MONTH, ISO_DAY, ISO_HOUR, ISO_MINUTE, ISO_SECOND, ISO_MILLISECOND, ISO_MICROSECOND, ISO_NANOSECOND, CALENDAR, EPOCHNANOSECONDS, GetSlot } from './slots';
import { DateTimeFormat } from './intl';
export class PlainDateTime {
    constructor(isoYearParam, isoMonthParam, isoDayParam, hourParam = 0, minuteParam = 0, secondParam = 0, millisecondParam = 0, microsecondParam = 0, nanosecondParam = 0, calendarParam = ES.GetISO8601Calendar()) {
        const isoYear = ES.ToIntegerThrowOnInfinity(isoYearParam);
        const isoMonth = ES.ToIntegerThrowOnInfinity(isoMonthParam);
        const isoDay = ES.ToIntegerThrowOnInfinity(isoDayParam);
        const hour = ES.ToIntegerThrowOnInfinity(hourParam);
        const minute = ES.ToIntegerThrowOnInfinity(minuteParam);
        const second = ES.ToIntegerThrowOnInfinity(secondParam);
        const millisecond = ES.ToIntegerThrowOnInfinity(millisecondParam);
        const microsecond = ES.ToIntegerThrowOnInfinity(microsecondParam);
        const nanosecond = ES.ToIntegerThrowOnInfinity(nanosecondParam);
        const calendar = ES.ToTemporalCalendar(calendarParam);
        // Note: if the arguments are not passed,
        //       ToIntegerThrowOnInfinity(undefined) will have returned 0, which will
        //       be rejected by RejectDateTime in CreateTemporalDateTimeSlots. This
        //       check exists only to improve the error message.
        if (arguments.length < 3) {
            throw new RangeError('missing argument: isoYear, isoMonth and isoDay are required');
        }
        ES.CreateTemporalDateTimeSlots(this, isoYear, isoMonth, isoDay, hour, minute, second, millisecond, microsecond, nanosecond, calendar);
    }
    get calendar() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, CALENDAR);
    }
    get year() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarYear(GetSlot(this, CALENDAR), this);
    }
    get month() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarMonth(GetSlot(this, CALENDAR), this);
    }
    get monthCode() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarMonthCode(GetSlot(this, CALENDAR), this);
    }
    get day() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDay(GetSlot(this, CALENDAR), this);
    }
    get hour() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, ISO_HOUR);
    }
    get minute() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, ISO_MINUTE);
    }
    get second() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, ISO_SECOND);
    }
    get millisecond() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, ISO_MILLISECOND);
    }
    get microsecond() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, ISO_MICROSECOND);
    }
    get nanosecond() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, ISO_NANOSECOND);
    }
    get era() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarEra(GetSlot(this, CALENDAR), this);
    }
    get eraYear() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarEraYear(GetSlot(this, CALENDAR), this);
    }
    get dayOfWeek() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDayOfWeek(GetSlot(this, CALENDAR), this);
    }
    get dayOfYear() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDayOfYear(GetSlot(this, CALENDAR), this);
    }
    get weekOfYear() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarWeekOfYear(GetSlot(this, CALENDAR), this);
    }
    get daysInWeek() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDaysInWeek(GetSlot(this, CALENDAR), this);
    }
    get daysInYear() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDaysInYear(GetSlot(this, CALENDAR), this);
    }
    get daysInMonth() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDaysInMonth(GetSlot(this, CALENDAR), this);
    }
    get monthsInYear() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarMonthsInYear(GetSlot(this, CALENDAR), this);
    }
    get inLeapYear() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarInLeapYear(GetSlot(this, CALENDAR), this);
    }
    with(temporalDateTimeLike, optionsParam = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        if (!ES.IsObject(temporalDateTimeLike)) {
            throw new TypeError('invalid argument');
        }
        ES.RejectObjectWithCalendarOrTimeZone(temporalDateTimeLike);
        const options = ES.GetOptionsObject(optionsParam);
        const calendar = GetSlot(this, CALENDAR);
        const fieldNames = ES.CalendarFields(calendar, [
            'day',
            'hour',
            'microsecond',
            'millisecond',
            'minute',
            'month',
            'monthCode',
            'nanosecond',
            'second',
            'year'
        ]);
        const props = ES.PrepareTemporalFields(temporalDateTimeLike, fieldNames, 'partial');
        if (!props) {
            throw new TypeError('invalid date-time-like');
        }
        let fields = ES.PrepareTemporalFields(this, fieldNames, []);
        fields = ES.CalendarMergeFields(calendar, fields, props);
        fields = ES.PrepareTemporalFields(fields, fieldNames, []);
        const { year, month, day, hour, minute, second, millisecond, microsecond, nanosecond } = ES.InterpretTemporalDateTimeFields(calendar, fields, options);
        return ES.CreateTemporalDateTime(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond, calendar);
    }
    withPlainTime(temporalTimeParam = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const year = GetSlot(this, ISO_YEAR);
        const month = GetSlot(this, ISO_MONTH);
        const day = GetSlot(this, ISO_DAY);
        const calendar = GetSlot(this, CALENDAR);
        if (temporalTimeParam === undefined)
            return ES.CreateTemporalDateTime(year, month, day, 0, 0, 0, 0, 0, 0, calendar);
        const temporalTime = ES.ToTemporalTime(temporalTimeParam);
        const hour = GetSlot(temporalTime, ISO_HOUR);
        const minute = GetSlot(temporalTime, ISO_MINUTE);
        const second = GetSlot(temporalTime, ISO_SECOND);
        const millisecond = GetSlot(temporalTime, ISO_MILLISECOND);
        const microsecond = GetSlot(temporalTime, ISO_MICROSECOND);
        const nanosecond = GetSlot(temporalTime, ISO_NANOSECOND);
        return ES.CreateTemporalDateTime(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond, calendar);
    }
    withPlainDate(temporalDateParam) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const temporalDate = ES.ToTemporalDate(temporalDateParam);
        const year = GetSlot(temporalDate, ISO_YEAR);
        const month = GetSlot(temporalDate, ISO_MONTH);
        const day = GetSlot(temporalDate, ISO_DAY);
        let calendar = GetSlot(temporalDate, CALENDAR);
        const hour = GetSlot(this, ISO_HOUR);
        const minute = GetSlot(this, ISO_MINUTE);
        const second = GetSlot(this, ISO_SECOND);
        const millisecond = GetSlot(this, ISO_MILLISECOND);
        const microsecond = GetSlot(this, ISO_MICROSECOND);
        const nanosecond = GetSlot(this, ISO_NANOSECOND);
        calendar = ES.ConsolidateCalendars(GetSlot(this, CALENDAR), calendar);
        return ES.CreateTemporalDateTime(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond, calendar);
    }
    withCalendar(calendarParam) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const calendar = ES.ToTemporalCalendar(calendarParam);
        return new PlainDateTime(GetSlot(this, ISO_YEAR), GetSlot(this, ISO_MONTH), GetSlot(this, ISO_DAY), GetSlot(this, ISO_HOUR), GetSlot(this, ISO_MINUTE), GetSlot(this, ISO_SECOND), GetSlot(this, ISO_MILLISECOND), GetSlot(this, ISO_MICROSECOND), GetSlot(this, ISO_NANOSECOND), calendar);
    }
    add(temporalDurationLike, options = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.AddDurationToOrSubtractDurationFromPlainDateTime('add', this, temporalDurationLike, options);
    }
    subtract(temporalDurationLike, options = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.AddDurationToOrSubtractDurationFromPlainDateTime('subtract', this, temporalDurationLike, options);
    }
    until(other, options = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.DifferenceTemporalPlainDateTime('until', this, other, options);
    }
    since(other, options = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.DifferenceTemporalPlainDateTime('since', this, other, options);
    }
    round(optionsParam) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        if (optionsParam === undefined)
            throw new TypeError('options parameter is required');
        const options = typeof optionsParam === 'string'
            ? ES.CreateOnePropObject('smallestUnit', optionsParam)
            : ES.GetOptionsObject(optionsParam);
        const smallestUnit = ES.GetTemporalUnit(options, 'smallestUnit', 'time', ES.REQUIRED, ['day']);
        const roundingMode = ES.ToTemporalRoundingMode(options, 'halfExpand');
        const maximumIncrements = {
            day: 1,
            hour: 24,
            minute: 60,
            second: 60,
            millisecond: 1000,
            microsecond: 1000,
            nanosecond: 1000
        };
        const roundingIncrement = ES.ToTemporalRoundingIncrement(options, maximumIncrements[smallestUnit], false);
        let year = GetSlot(this, ISO_YEAR);
        let month = GetSlot(this, ISO_MONTH);
        let day = GetSlot(this, ISO_DAY);
        let hour = GetSlot(this, ISO_HOUR);
        let minute = GetSlot(this, ISO_MINUTE);
        let second = GetSlot(this, ISO_SECOND);
        let millisecond = GetSlot(this, ISO_MILLISECOND);
        let microsecond = GetSlot(this, ISO_MICROSECOND);
        let nanosecond = GetSlot(this, ISO_NANOSECOND);
        ({ year, month, day, hour, minute, second, millisecond, microsecond, nanosecond } = ES.RoundISODateTime(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond, roundingIncrement, smallestUnit, roundingMode));
        return ES.CreateTemporalDateTime(year, month, day, hour, minute, second, millisecond, microsecond, nanosecond, GetSlot(this, CALENDAR));
    }
    equals(otherParam) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const other = ES.ToTemporalDateTime(otherParam);
        for (const slot of [
            ISO_YEAR,
            ISO_MONTH,
            ISO_DAY,
            ISO_HOUR,
            ISO_MINUTE,
            ISO_SECOND,
            ISO_MILLISECOND,
            ISO_MICROSECOND,
            ISO_NANOSECOND
        ]) {
            const val1 = GetSlot(this, slot);
            const val2 = GetSlot(other, slot);
            if (val1 !== val2)
                return false;
        }
        return ES.CalendarEquals(GetSlot(this, CALENDAR), GetSlot(other, CALENDAR));
    }
    toString(optionsParam = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const options = ES.GetOptionsObject(optionsParam);
        const { precision, unit, increment } = ES.ToSecondsStringPrecision(options);
        const showCalendar = ES.ToShowCalendarOption(options);
        const roundingMode = ES.ToTemporalRoundingMode(options, 'trunc');
        return ES.TemporalDateTimeToString(this, precision, showCalendar, { unit, increment, roundingMode });
    }
    toJSON() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.TemporalDateTimeToString(this, 'auto');
    }
    toLocaleString(locales = undefined, options = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return new DateTimeFormat(locales, options).format(this);
    }
    valueOf() {
        throw new TypeError('use compare() or equals() to compare Temporal.PlainDateTime');
    }
    toZonedDateTime(temporalTimeZoneLike, optionsParam = undefined) {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const timeZone = ES.ToTemporalTimeZone(temporalTimeZoneLike);
        const options = ES.GetOptionsObject(optionsParam);
        const disambiguation = ES.ToTemporalDisambiguation(options);
        const instant = ES.BuiltinTimeZoneGetInstantFor(timeZone, this, disambiguation);
        return ES.CreateTemporalZonedDateTime(GetSlot(instant, EPOCHNANOSECONDS), timeZone, GetSlot(this, CALENDAR));
    }
    toPlainDate() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.TemporalDateTimeToDate(this);
    }
    toPlainYearMonth() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const calendar = GetSlot(this, CALENDAR);
        const fieldNames = ES.CalendarFields(calendar, ['monthCode', 'year']);
        const fields = ES.PrepareTemporalFields(this, fieldNames, []);
        return ES.CalendarYearMonthFromFields(calendar, fields);
    }
    toPlainMonthDay() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        const calendar = GetSlot(this, CALENDAR);
        const fieldNames = ES.CalendarFields(calendar, ['day', 'monthCode']);
        const fields = ES.PrepareTemporalFields(this, fieldNames, []);
        return ES.CalendarMonthDayFromFields(calendar, fields);
    }
    toPlainTime() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return ES.TemporalDateTimeToTime(this);
    }
    getISOFields() {
        if (!ES.IsTemporalDateTime(this))
            throw new TypeError('invalid receiver');
        return {
            calendar: GetSlot(this, CALENDAR),
            isoDay: GetSlot(this, ISO_DAY),
            isoHour: GetSlot(this, ISO_HOUR),
            isoMicrosecond: GetSlot(this, ISO_MICROSECOND),
            isoMillisecond: GetSlot(this, ISO_MILLISECOND),
            isoMinute: GetSlot(this, ISO_MINUTE),
            isoMonth: GetSlot(this, ISO_MONTH),
            isoNanosecond: GetSlot(this, ISO_NANOSECOND),
            isoSecond: GetSlot(this, ISO_SECOND),
            isoYear: GetSlot(this, ISO_YEAR)
        };
    }
    static from(item, optionsParam = undefined) {
        const options = ES.GetOptionsObject(optionsParam);
        if (ES.IsTemporalDateTime(item)) {
            ES.ToTemporalOverflow(options); // validate and ignore
            return ES.CreateTemporalDateTime(GetSlot(item, ISO_YEAR), GetSlot(item, ISO_MONTH), GetSlot(item, ISO_DAY), GetSlot(item, ISO_HOUR), GetSlot(item, ISO_MINUTE), GetSlot(item, ISO_SECOND), GetSlot(item, ISO_MILLISECOND), GetSlot(item, ISO_MICROSECOND), GetSlot(item, ISO_NANOSECOND), GetSlot(item, CALENDAR));
        }
        return ES.ToTemporalDateTime(item, options);
    }
    static compare(oneParam, twoParam) {
        const one = ES.ToTemporalDateTime(oneParam);
        const two = ES.ToTemporalDateTime(twoParam);
        for (const slot of [
            ISO_YEAR,
            ISO_MONTH,
            ISO_DAY,
            ISO_HOUR,
            ISO_MINUTE,
            ISO_SECOND,
            ISO_MILLISECOND,
            ISO_MICROSECOND,
            ISO_NANOSECOND
        ]) {
            const val1 = GetSlot(one, slot);
            const val2 = GetSlot(two, slot);
            if (val1 !== val2)
                return ES.ComparisonResult(val1 - val2);
        }
        return 0;
    }
}
Symbol.toStringTag;
MakeIntrinsicClass(PlainDateTime, 'Temporal.PlainDateTime');
//# sourceMappingURL=plaindatetime.js.map